import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { DATE_FORMAT } from '@root/shared'
import { useTranslation } from '@root/shared/localization'

import { TradePositionDetailsTableRow as Row } from './trade-position-details-table-row'

import type { AggregatedPositionModel } from '@root/entities/position'
import type { WalletModel } from '@root/entities/wallet/atoms'

type Props = {
  position: AggregatedPositionModel
}

export const TradePositionDetailsTable: React.FC<Props> = ({ position }) => {
  const { t } = useTranslation()
  // TODO: Check after redesigns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wallet: WalletModel | undefined = undefined as any
  const nativeCurrency = CurrencyModel.nativeOf(position.network)

  const [targetPriceNative] = useAtom(position.targetPriceNative)
  const [targetMarketCapUsd] = useAtom(position.targetMarketCapUsd)
  const [entryPriceNative] = useAtom(position.entryPriceNative)
  const [entryMarketCapUsd] = useAtom(position.entryMarketCapUsd)
  const [closePriceNative] = useAtom(position.closePriceNative)
  const [closeMarketCapUsd] = useAtom(position.closeMarketCapUsd)
  const [createdAt] = useAtom(position.createdAt)
  const [closedAt] = useAtom(position.closedAt)

  const renderLimitOrderInfo = () => {
    // TODO: Check after redesigns
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const limitOrderInfo: any = undefined

    if (!limitOrderInfo) {
      return undefined
    }

    const renderTakeProfit = () => {
      const { type, valueHigh } = limitOrderInfo.limitOrderSettings

      const label =
        type === 'absolute' ? (
          <span>
            {t('price')} {'>= '}
          </span>
        ) : (
          <span>
            {t('market-cap')} {'>= '}
          </span>
        )

      return (
        <Row label={t('trading.take_profit')}>
          {label}
          <Numeric
            value={valueHigh || '0'}
            preset="amount-precise"
            currency="$"
          />
        </Row>
      )
    }

    const renderStopLoss = () => {
      const { type, valueLow } = limitOrderInfo.limitOrderSettings

      const label =
        type === 'absolute' ? (
          <span>
            {t('price')} {'<= '}
          </span>
        ) : (
          <span>
            {t('market-cap')} {'<= '}
          </span>
        )

      return (
        <Row label={t('trading.stop_loss')}>
          {label}
          <Numeric
            value={valueLow || '0'}
            preset="amount-precise"
            currency="$"
          />
        </Row>
      )
    }

    return (
      <>
        {renderTakeProfit()}
        {renderStopLoss()}
      </>
    )
  }

  return (
    <Root>
      {renderLimitOrderInfo()}
      <Row label={t('common.created')} marketCapacity={entryMarketCapUsd}>
        <Numeric
          value={entryPriceNative}
          preset="currency-amount-short"
          currency={nativeCurrency.ticker}
        />
      </Row>
      {targetPriceNative && targetMarketCapUsd ? (
        <Row
          label={t('trade.target-price')}
          marketCapacity={targetMarketCapUsd}
        >
          <Numeric
            value={targetPriceNative}
            preset="currency-amount-short"
            currency={nativeCurrency.ticker}
          />
        </Row>
      ) : undefined}

      {closePriceNative && closeMarketCapUsd ? (
        <Row
          label={t('trade.trigger-price')}
          marketCapacity={closeMarketCapUsd}
        >
          <Numeric
            value={closePriceNative}
            preset="currency-amount-short"
            currency={nativeCurrency.ticker}
          />
        </Row>
      ) : undefined}
      {/* <Row
        label={t('common.status')}
        color={TRADE_POSITION_STATUS_TO_COLOR[status]}
      >
        {t(TRADE_POSITION_STATUS_TO_TRANS[status])}
      </Row> */}
      {wallet ? (
        <Row label={t('wallet_one')}>
          <WalletName>
            <WalletModelShort wallet={wallet} size="sm" />
            <span>
              (
              <WalletAddressTruncated walletAddress={wallet.address} />)
            </span>
          </WalletName>
        </Row>
      ) : undefined}
      <Group>
        <Row label={t('common.created')}>
          {DateF.format(createdAt, DATE_FORMAT.MMMM_d__HH_mm_ss)}
        </Row>
        {/* <TradePositionDetailsTableRowExpiry position={position} /> */}
      </Group>
      {closedAt ? (
        <Row label={t('position.closed')}>
          {DateF.format(closedAt, DATE_FORMAT.MMMM_d__HH_mm_ss)}
        </Row>
      ) : undefined}
    </Root>
  )
}

const Root = styled.section`
  display: grid;
  align-content: start;
  gap: 8px;
`

const WalletName = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: center;
  align-items: center;
  gap: 4px;
`

const Group = styled.div``
